// app/src/screens/Ideas.jsx — the ideas gate (handoff §3), pixel-close. CREATE-2: REAL now.
// The `ideate` verb runs as an async job through the draft (draftIdeate); we watch it, then
// render the ASSEMBLED draft's idea sets verbatim (cards from the server — hook/premise/why/
// tags + the RECOMMENDED ring on `recommended`). "More ideas" and the steer box are new ideate
// invocations; each may enqueue free (in-allowance) or return the standing 20-credit confirm
// gate / out-of-credits state. The price line reads /v1/me allowances. Zero authoring logic
// client-side — the SPA only consumes.

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Clock, Crop, Globe, SlidersHorizontal, TriangleAlert } from 'lucide-react';
import { navigate } from '@common/router.js';
import { watchJob } from '@common/progress.js';
import { getDraft, draftIdeate, updateDraft } from '@common/drafts.js';
import { newIdemKey } from '@common/commands.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useFlow } from '../lib/flow.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useToast } from '../lib/toast.jsx';
import { useTopUp } from '../lib/topup.jsx';
import { StepIndicator } from '../components/ui.jsx';
import { nextAspect } from '../lib/aspect.js';
import { nextLength, fmtLength, nextLanguage, langName, effectiveSettings } from '../lib/settings.js';

const LIVE = ['queued', 'running'];
const ACCENTS = ['coral', 'violet', 'tangerine'];

export default function Ideas() {
  const { t } = useI18n();
  const { api, me, refreshMe } = useSession();
  const { draftId, setPick } = useFlow();
  const mobile = useIsMobile();
  const toast = useToast();
  const openTopUp = useTopUp();
  const [draft, setDraft] = useState(null);
  const [viewIdx, setViewIdx] = useState(0);
  const [watching, setWatching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState(null); // { kind:'confirm', quote, steer } | { kind:'ooc', credits_required, credits_balance }
  const [steerText, setSteerText] = useState('');
  const [streamCards, setStreamCards] = useState([]); // P1-BRAIN Q5: cards arriving live as `card` step-events
  const [err, setErr] = useState(null);
  const intentKey = useRef(null);
  const stopRef = useRef(null);
  const reQuoted = useRef(false);
  const failedSig = useRef(null); // F6: the intent signature (steer text) of the last network-failed press, or null
  const warnedSet = useRef(null); // SETTINGS-THREAD: the set whose clamp warning we've already toasted

  const refresh = async () => {
    const r = await getDraft(api, draftId);
    if (r.kind === 'ok') { setDraft(r.body); return r.body; }
    setErr(r.error || t('common.error'));
    return null;
  };

  function watch(jobId, jumpToNewest) {
    if (stopRef.current) stopRef.current();
    setWatching(true);
    setStreamCards([]);
    stopRef.current = watchJob({
      api, jobId,
      // rtdb:true subscribes to the worker's live step feed (progress/<job>/steps) — without it
      // onStep NEVER fires (watchJob only wires it under RTDB) and the row-poll fallback carries
      // the coarse state alone. Degrades safely to polling if RTDB is unconfigured/locked.
      rtdb: true,
      // P1-BRAIN Q5: the ideate reply streams; the engine emits one `card` step-event per
      // completed IDEA block — render them progressively so the ~25s turn feels like browsing.
      onStep: (s) => {
        if (s && s.step === 'card' && s.title) {
          setStreamCards((prev) => {
            const next = prev.filter((c) => c.index !== s.index);
            next.push({ index: s.index, title: s.title, medium: s.medium });
            return next.sort((a, b) => a.index - b.index);
          });
        }
      },
      onState: async ({ state }) => {
        if (['succeeded', 'failed', 'refused', 'cancelled'].includes(state)) {
          setWatching(false);
          setStreamCards([]);
          const d = await refresh();
          refreshMe();
          if (d && jumpToNewest) setViewIdx(Math.max(0, d.idea_sets.length - 1));
        }
      },
    });
  }

  useEffect(() => {
    if (!draftId) { navigate('/'); return; }
    (async () => {
      const d = await refresh();
      if (!d) return;
      setViewIdx(Math.max(0, d.idea_sets.length - 1));
      const live = d.idea_sets.filter((s) => LIVE.includes(s.state)).pop();
      if (live) watch(live.job_id, true);
    })();
    return () => { if (stopRef.current) stopRef.current(); };
  }, [draftId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SETTINGS-THREAD: surface the 20-minute clamp honestly — toast once when the newest set carries
  // a LENGTH_CHANGED warning (e.g. a "25 min" ask capped to 20). Warnings are engine DATA.
  useEffect(() => {
    if (!draft) return;
    const s = (draft.idea_sets || []).filter((x) => x.set).pop();
    const w = s && s.set && s.set.warnings;
    if (!w || !w.length || warnedSet.current === s.job_id) return;
    warnedSet.current = s.job_id;
    const clamp = w.find((x) => x.code === 'LENGTH_CHANGED' && x.new_value);
    if (clamp) toast(t('settings.clamped', { length: fmtLength(clamp.new_value) }));
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  // one ideate invocation (a new SET). confirm=false first (free enqueues; a gate returns the
  // price without spending); confirm=true re-sends the SAME intent + steer through the gate.
  async function runIdeate({ steer = null, confirm = false } = {}) {
    if (busy || watching || !draftId) return; // F6: never start a second ideate while one is live (orphans the watcher, burns an allowance unit)
    setBusy(true); setErr(null);
    // a fresh press is a fresh intent — mint a new key. BUT if the PREVIOUS press of the SAME intent
    // (same steer text) failed at the network (no response), REUSE its key so a retry dedups
    // server-side instead of minting a second allowance unit (CATCHUP F6). Scoping to the intent
    // signature means a steer after a failed "more" mints a fresh key (never dedups into the
    // wrong set). A confirm always reuses the standing key.
    const sig = steer || ''; // plain "more" = '', a steer = its text
    if (!confirm && !(failedSig.current === sig && intentKey.current)) intentKey.current = newIdemKey();
    failedSig.current = null;
    const r = await draftIdeate(api, draftId, { steer: steer || null, confirm, idempotencyKey: intentKey.current });
    setBusy(false);
    if (r.kind === 'ok') {
      setGate(null); setSteerText(''); reQuoted.current = false;
      const d = await refresh(); refreshMe();
      if (d) { const live = d.idea_sets.filter((s) => LIVE.includes(s.state)).pop(); if (live) watch(live.job_id, true); else setViewIdx(Math.max(0, d.idea_sets.length - 1)); }
    } else if (r.kind === 'needs_confirm') {
      setGate({ kind: 'confirm', quote: r.quote, steer: steer || null });
    } else if (r.kind === 'insufficient_credits') {
      setGate({ kind: 'ooc', credits_required: r.credits_required, credits_balance: r.credits_balance });
    } else if (r.kind === 'refused') {
      // a concurrent set moved the free/paid boundary — re-quote once at the honest price
      if (r.body && r.body.allowance_changed && !reQuoted.current) { reQuoted.current = true; return runIdeate({ steer }); }
      setErr(r.error || t('common.error'));
    } else {
      failedSig.current = sig; // network/unknown drop — the server MAY have enqueued; keep the key for a same-intent retry
      setErr(r.error || t('common.error'));
    }
  }

  if (err && !draft) return <div className="view"><p className="muted" style={{ color: 'var(--coral)' }}>{err}</p></div>;
  if (!draft) return <div className="view"><p className="muted">{t('common.loading')}</p></div>;

  const sets = draft.idea_sets || [];
  const current = sets[Math.min(viewIdx, Math.max(0, sets.length - 1))] || null;
  // SETTINGS-THREAD: the three chips render the SETTLED values — an explicit draft chip wins, else
  // the engine-resolved value on the newest set (parse/default), else the default. All adjustable.
  const eff = effectiveSettings(draft.settings, current && current.set && current.set.settings);
  const aspect = eff.aspect;

  // patch the draft's settings, optimistically; revert + toast on a 409 (an approved draft).
  const patchSettings = async (patch, onOk) => {
    const prev = draft.settings || {};
    setDraft((d) => ({ ...d, settings: { ...(d.settings || {}), ...patch } }));
    const r = await updateDraft(api, draftId, { settings: { ...prev, ...patch } });
    if (r.kind !== 'ok') { setDraft((d) => ({ ...d, settings: prev })); toast(r.error || t('common.error')); return; }
    if (onOk) onOk();
  };
  const cycleAspect = () => patchSettings({ aspect: nextAspect(aspect) }); // aspect change → DATA-ONLY (no re-ideate)
  const cycleLanguage = () => patchSettings({ language: nextLanguage(eff.language) }); // language change → DATA-ONLY
  const cycleLength = () => {
    const next = nextLength(eff.length_s);
    // length change → the ideas MUST be re-fit: persist the chip, then a MODIFIED-set follow-up.
    patchSettings({ length_s: next }, () => { if (!watching && !busy) runIdeate({ steer: t('settings.refitSteer', { length: fmtLength(next) }) }); });
  };
  const chipVal = (v, isAssumed) => isAssumed ? `${v} ${t('settings.assumed')}` : v;
  const settings = [
    { Icon: Clock, color: 'var(--coral)', label: t('settings.length'), value: chipVal(fmtLength(eff.length_s), eff.assumed.length), onClick: cycleLength },
    { Icon: Crop, color: 'var(--violet)', label: t('settings.aspect'), value: chipVal(aspect, eff.assumed.aspect), onClick: cycleAspect },
    { Icon: Globe, color: 'var(--mint)', label: t('settings.language'), value: chipVal(langName(eff.language), eff.assumed.language), onClick: cycleLanguage },
  ];

  // the allowance row (used only by the out-of-credits gate now — ideas never show a price/credit
  // note; the credit gate lives at video generation, Daniel product law 2026-07-08)
  const allowance = (me && me.allowances || []).find((a) => a.main_key === 'ideation') || null;

  const use = (card) => { if (!current) return; setPick({ setJobId: current.job_id, cardId: card.id }); navigate('/create/script'); };

  const settingsBar = mobile ? (
    <div className="h-scroll" style={{ margin: '14px 0 16px' }}>
      {settings.map((s, i) => (
        <button key={i} className="q-btn settings-chip" style={{ flex: 'none', borderRadius: 11 }} onClick={s.onClick}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: s.color }} />{s.value} <span className="caret">▾</span>
        </button>
      ))}
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '8px 8px 8px 14px', margin: '24px 0 20px', flexWrap: 'wrap' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, letterSpacing: '.08em' }} className="eyebrow"><SlidersHorizontal size={14} color="var(--text-faint)" strokeWidth={2} />{t('settings.title')}</span>
      <span style={{ width: 1, height: 22, background: 'var(--border)', margin: '0 3px' }} />
      {settings.map((s, i) => (
        <button key={i} className="q-btn press settings-chip" onClick={s.onClick}>
          <s.Icon size={14} color={s.color} strokeWidth={2} /><span className="k">{s.label}</span>{s.value}<span className="caret">▾</span>
        </button>
      ))}
    </div>
  );

  // ---- the gate card (20-credit confirm / out-of-credits) — the standing money surface ----
  const gateCard = gate && (
    <div className="q-card" style={{ maxWidth: 520, margin: '18px auto 0', padding: 18, borderRadius: 16, border: '1px solid var(--coral-line)', textAlign: 'center' }}>
      {gate.kind === 'ooc' ? (
        <>
          <h4 className="h-card" style={{ margin: '0 0 6px' }}>{t('ideas.oocTitle')}</h4>
          <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>{t('script.needsBalance', { required: gate.credits_required, balance: gate.credits_balance })}</p>
          <button className="q-btn press" onClick={openTopUp} style={{ background: 'var(--coral)', color: '#fff', padding: '12px 22px', borderRadius: 12, font: '700 13.5px var(--font-body)', boxShadow: '0 6px 16px rgba(255,81,71,.28)' }}>{t('script.outOfCredits')}</button>
        </>
      ) : (
        <>
          <h4 className="h-card" style={{ margin: '0 0 6px' }}>{gate.steer ? t('ideas.confirmSteerTitle') : t('ideas.confirmMoreTitle')}</h4>
          <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>{t('ideas.confirmPrice', { credits: (gate.quote && gate.quote.credits && gate.quote.credits.credits) || (allowance && allowance.overflow_credits) || '—' })}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button className="q-btn press btn-ghost" onClick={() => setGate(null)}>{t('common.cancel')}</button>
            <button className="q-btn press" disabled={busy} onClick={() => runIdeate({ steer: gate.steer, confirm: true })} style={{ background: 'var(--coral)', color: '#fff', padding: '12px 22px', borderRadius: 12, font: '700 13.5px var(--font-body)', opacity: busy ? 0.6 : 1 }}>{t('ideas.confirmSpend')}</button>
          </div>
        </>
      )}
    </div>
  );

  // ---- the cards for the viewed set ----
  const loadingSet = watching && (!current || LIVE.includes(current.state));
  const failedSet = current && current.state === 'failed';
  let body;
  if (loadingSet) {
    body = (
      <div style={{ textAlign: 'center', padding: streamCards.length ? '24px 0' : '48px 0' }}>
        <div style={{ display: 'inline-flex', gap: 9, alignItems: 'flex-end', height: 22 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ width: 12, height: 12, borderRadius: 999, background: 'var(--coral)', animation: `bounceDot 1s ease-in-out ${i * 0.16}s infinite` }} />
          ))}
        </div>
        <p className="muted" style={{ marginTop: 16, font: '600 14px var(--font-body)' }}>{t('ideas.dreaming')}</p>
        {streamCards.length > 0 && (
          <div style={{ maxWidth: 520, margin: '18px auto 0', display: 'grid', gap: 8 }}>
            {streamCards.map((c) => (
              <div key={c.index} className="q-card" style={{ padding: '10px 14px', borderRadius: 12, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="mono" style={{ font: '700 10px var(--font-mono)', color: 'var(--coral)' }}>{c.index >= 1 && c.index <= 26 ? String.fromCharCode(64 + c.index) : String(c.index)}</span>
                <span style={{ font: '600 13px var(--font-body)', color: 'var(--ink)', flex: 1 }}>{c.title}</span>
                {c.medium && <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)' }}>{c.medium}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  } else if (failedSet) {
    body = (
      <div className="q-card" style={{ maxWidth: 520, margin: '18px auto', padding: 20, borderRadius: 16, textAlign: 'center' }}>
        <TriangleAlert size={26} color="var(--coral)" strokeWidth={2.2} style={{ margin: '0 auto 10px', display: 'block' }} />
        <h4 className="h-card" style={{ margin: '0 0 6px' }}>{t('ideas.failedTitle')}</h4>
        <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>{t('ideas.failedNote')}</p>
        <button className="q-btn press" onClick={() => runIdeate({})} style={{ background: 'var(--coral)', color: '#fff', padding: '11px 20px', borderRadius: 12, font: '700 13px var(--font-body)' }}>{t('generating.tryAgain')}</button>
      </div>
    );
  } else if (current && current.set && current.set.cards) {
    body = (
      <div className="idea-grid" style={mobile ? { gridTemplateColumns: '1fr' } : undefined}>
        {current.set.cards.map((card, i) => {
          const rec = !!card.recommended;
          const acc = rec ? 'mint' : ACCENTS[i % ACCENTS.length];
          const accent = `var(--${acc})`;
          return (
            <div key={card.id} className={'idea-card' + (rec ? ' rec' : ' lift')}>
              <div className="idea-strip" style={{ background: accent }} />
              <div className="idea-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="mono" style={{ font: '700 10px var(--font-mono)', color: accent, letterSpacing: '.1em', textTransform: 'uppercase' }}>{t('ideas.ideaLabel', { letter: i < 26 ? String.fromCharCode(65 + i) : String(i + 1) })}</span>
                  {rec && <span className="mono" style={{ font: '700 9.5px var(--font-mono)', color: '#fff', background: 'var(--mint)', borderRadius: 999, padding: '4px 9px', textTransform: 'uppercase' }}>{t('ideas.recommended')}</span>}
                </div>
                <h4 className="h-card" style={{ margin: '8px 0' }}>{card.hook}</h4>
                <p className="muted" style={{ font: '400 13px/1.5 var(--font-body)', margin: 0 }}>{card.premise}</p>
                {card.why && (
                  <div style={{ margin: '13px 0 4px' }}>
                    <span className="mono" style={{ font: '700 9.5px var(--font-mono)', color: 'var(--placeholder-warm)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('ideas.why')}</span>
                    <p className="muted" style={{ font: '400 12px/1.45 var(--font-body)', margin: '5px 0 0' }}>{card.why}</p>
                  </div>
                )}
                {Array.isArray(card.tags) && card.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                    {card.tags.map((tag) => (
                      <span key={tag} className="mono" style={{ font: '600 10.5px var(--font-mono)', color: rec ? '#fff' : accent, background: rec ? 'var(--mint)' : `var(--${acc}-tint)`, borderRadius: 999, padding: '5px 10px' }}>{tag}</span>
                    ))}
                  </div>
                )}
                <div style={{ flex: 1 }} />
                <div style={{ marginTop: 16 }}>
                  <button className="q-btn press" onClick={() => use(card)} style={{ width: '100%', background: accent, color: '#fff', padding: 11, borderRadius: 999, font: '700 12.5px var(--font-body)', boxShadow: rec ? 'var(--shadow-mint)' : 'none' }}>
                    {card.faithful ? 'Script it' : `${t('ideas.use')}${rec ? ' →' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  } else if (sets.length === 0) {
    // no set yet (the first ideate was gated out of the free allowance) — a Get-ideas CTA
    body = (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p className="muted" style={{ margin: '0 0 16px', fontSize: 14 }}>{t('ideas.noneYet')}</p>
        <button className="q-btn press btn-primary" disabled={busy} onClick={() => runIdeate({})}>{t('ideas.getFirst')} →</button>
      </div>
    );
  } else {
    body = <div style={{ textAlign: 'center', padding: '40px 0' }}><p className="muted">{t('common.loading')}</p></div>;
  }

  return (
    <div className="view">
      <StepIndicator current="ideas" size={mobile ? 'sm' : 'lg'} />
      {settingsBar}

      <h2 className="h-section" style={{ margin: mobile ? '0 0 4px' : '24px 0 4px' }}>{t('ideas.title')}</h2>
      <p className="muted" style={{ margin: '0 0 16px', fontSize: mobile ? 13 : 14, lineHeight: 1.5 }}>{t('ideas.subcopy')}</p>

      {gateCard}
      {body}
      {err && draft && <p className="muted" style={{ color: 'var(--coral)', textAlign: 'center', marginTop: 12 }}>{err}</p>}

      {/* IDEA SETS dot-nav */}
      {sets.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 20 }}>
          <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faintest)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('ideas.ideaSets')}</span>
          {sets.map((s, b) => (
            <button key={s.job_id} className="q-btn" onClick={() => setViewIdx(b)} style={{ background: 'transparent', padding: 5, border: 'none', cursor: 'pointer' }}>
              <span style={{ display: 'block', width: viewIdx === b ? 24 : 10, height: 8, borderRadius: 999, background: viewIdx === b ? 'var(--coral)' : 'var(--border-strong)', transition: 'width .15s var(--ease)' }} />
            </button>
          ))}
        </div>
      )}

      {/* steer + more ideas */}
      <div style={{ display: 'flex', gap: 10, marginTop: 18, maxWidth: 880, marginInline: 'auto', flexDirection: mobile ? 'column' : 'row' }}>
        <form style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 999, padding: '8px 8px 8px 18px' }} onSubmit={(e) => { e.preventDefault(); if (steerText.trim()) runIdeate({ steer: steerText.trim() }); }}>
          <Sparkles size={16} color="var(--coral)" strokeWidth={2} />
          <input value={steerText} onChange={(e) => setSteerText(e.target.value)} placeholder={t('ideas.steerPlaceholder')} style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', font: '500 13px var(--font-body)', color: 'var(--ink)' }} />
          <button type="submit" className="q-btn press" disabled={busy || watching || !steerText.trim()} style={{ background: 'var(--fill-ghost)', color: 'var(--ink)', padding: '8px 14px', borderRadius: 999, font: '700 12px var(--font-body)', opacity: (busy || watching || !steerText.trim()) ? 0.5 : 1 }}>{t('ideas.steerSend')}</button>
        </form>
        <button className="q-btn press" disabled={busy || watching} onClick={() => runIdeate({})} style={{ background: 'var(--ink)', color: '#fff', padding: mobile ? 12 : '12px 22px', borderRadius: mobile ? 14 : 999, font: '700 13px var(--font-body)', opacity: (busy || watching) ? 0.6 : 1 }}>{t('ideas.more')}</button>
      </div>
    </div>
  );
}
